/**
 * API Client 설정
 * Axios 기반 HTTP 클라이언트
 */

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { message } from 'antd';
import { trackEvent } from '../../lib/analytics';
import { captureFrontendException, captureFrontendMessage } from '../../lib/observability';
import { clearCurrentAuthSession } from '../../lib/authSession';

const notifyError = (text: string) => {
  if (process.env.NODE_ENV !== 'test') {
    message.error(text);
  }
};

// API Base URL 설정
const getApiBaseUrl = (): string => {
  const envUrl = (process.env.REACT_APP_API_BASE_URL || '').trim();
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return envUrl || 'http://localhost:9000';
};

export const API_BASE_URL = getApiBaseUrl();

const normalizePath = (url?: string): string => {
  if (!url) return '';
  return url.replace(/^https?:\/\/[^/]+/i, '');
};

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const item = document.cookie.split('; ').find((cookie) => cookie.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : null;
};

const isUnsafeMethod = (method?: string): boolean => {
  return ['post', 'put', 'patch', 'delete'].includes((method || '').toLowerCase());
};

const mappedSuccessEvent = (method?: string, url?: string): string | null => {
  const m = (method || '').toLowerCase();
  const path = normalizePath(url);

  if (m === 'post' && path.includes('/api/v1/auth/login')) return 'login_complete';
  if (m === 'post' && path.includes('/api/v1/auth/register')) return 'signup_complete';
  if (m === 'post' && path.includes('/api/v1/projects')) return 'project_create_success';
  if (m === 'post' && path.includes('/api/v1/content-v5/create-manual')) return 'content_create_success';
  if (m === 'post' && path.includes('/api/v1/content-v5/create-from-keyword')) return 'content_create_success';
  if (m === 'post' && path.includes('/api/v1/content-v5/create-from-trend')) return 'content_create_success';
  return null;
};

const isTimeoutError = (error: AxiosError<ApiErrorResponse>): boolean => {
  return error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout');
};

const isExpectedLongRunningEndpoint = (url?: string): boolean => {
  const path = normalizePath(url);
  return [
    '/api/v1/content-v5/create-manual',
    '/api/v1/content-v5/create-from-keyword',
    '/api/v1/content-v5/create-from-trend',
    '/api/v1/content-v5/',
  ].some((endpoint) => path.includes(endpoint)) && (
    path.includes('/render') ||
    path.includes('/create-manual') ||
    path.includes('/create-from-keyword') ||
    path.includes('/create-from-trend')
  );
};

export const shouldCaptureApiException = (error: AxiosError<ApiErrorResponse>): boolean => {
  const status = error.response?.status;

  // 4xx responses are expected product/auth validation outcomes and should not
  // pollute Sentry. They are still tracked through GA and surfaced in the UI.
  if (typeof status === 'number') {
    return status >= 500;
  }

  // AI 생성/렌더링은 long-running product action이다. timeout은 GA 이벤트와
  // 사용자 안내로 추적하고 Sentry exception noise로는 올리지 않는다.
  if (isTimeoutError(error) && isExpectedLongRunningEndpoint(error.config?.url)) {
    return false;
  }

  // No response usually means timeout, CORS, DNS, or network failure.
  return true;
};

// Axios 인스턴스 생성
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // httpOnly 쿠키 전송을 위해 필수
});

// Request Interceptor - 요청 전 처리
apiClient.interceptors.request.use(
  (config) => {
    // httpOnly 쿠키는 자동으로 전송됨 (withCredentials: true)
    // Authorization 헤더/localStorage 토큰 경로는 보안상 사용하지 않음
    const csrfToken = readCookie('csrf_token');
    if (csrfToken && isUnsafeMethod(config.method)) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    // 요청 로깅 (개발 환경)
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.data);
    }

    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response Interceptor - 응답 후 처리
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const successEvent = mappedSuccessEvent(response.config?.method, response.config?.url);
    if (successEvent) {
      trackEvent(successEvent, {
        endpoint: normalizePath(response.config?.url),
        method: (response.config?.method || 'get').toUpperCase(),
      });
    }
    // 응답 로깅 (개발 환경)
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
    }

    // 백엔드 응답 구조: { success: boolean, data: any, message: string }
    // data 필드를 자동으로 추출하여 반환
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return response.data.data;
    }

    return response.data;
  },
  (error: AxiosError<ApiErrorResponse>) => {
    // LoginPage renders actionable inline errors, including older API wording.
    // Keep the rejection and telemetry, but avoid a second, conflicting toast.
    const notifyRequestError = normalizePath(error.config?.url).split('?')[0] === '/api/v1/auth/login'
      ? (_text: string) => undefined
      : notifyError;
    if (shouldCaptureApiException(error)) {
      captureFrontendException(error, {
        endpoint: normalizePath(error.config?.url),
        method: (error.config?.method || 'get').toUpperCase(),
        status: error.response?.status || 0,
      });
    }
    trackEvent('api_request_failed', {
      endpoint: normalizePath(error.config?.url),
      method: (error.config?.method || 'get').toUpperCase(),
      status: error.response?.status || 0,
      message: error.response?.data?.message || error.message,
      error_code: error.response?.data?.error || 'unknown_api_error',
    });
    // 에러 로깅: 4xx는 사용자 입력/권한 검증 결과이므로 production console.error를 남기지 않는다.
    const isExpectedClientResponse = typeof error.response?.status === 'number' &&
      error.response.status >= 400 &&
      error.response.status < 500;
    if (!isExpectedClientResponse) {
      console.error('[API Response Error]', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
      });
    }

    // HTTP 상태 코드별 처리
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Do not leave a stale header or cached user profile on screen after
          // the httpOnly session cookie expires or is revoked.
          clearCurrentAuthSession();
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('ai-marketing:session-expired'));
          }
          // auth/me 401은 미인증 상태 체크용 — 토스트 없이 조용히 처리
          if (!normalizePath(error.config?.url)?.includes('/auth/me')) {
            notifyRequestError(data?.message || '인증이 필요합니다. 다시 로그인해주세요.');
          }
          break;

        case 403:
          // 권한 없음 / 초대·라이선스 검증 실패
          notifyRequestError(data?.message || '접근 권한이 없습니다.');
          break;

        case 404:
          // 리소스 없음
          notifyRequestError(data?.message || '요청한 리소스를 찾을 수 없습니다.');
          break;

        case 500:
        case 502:
        case 503:
          // 서버 에러
          captureFrontendMessage('api_server_error', {
            endpoint: normalizePath(error.config?.url),
            status,
            message: data?.message || error.message,
          });
          notifyRequestError(data?.message || '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
          break;

        default:
          // 기타 에러
          notifyRequestError(data?.message || error.message || '알 수 없는 오류가 발생했습니다.');
      }

      // 에러 객체 반환 (호출하는 쪽에서 추가 처리 가능)
      return Promise.reject({
        status,
        message: data?.message || error.message,
        data: data,
      });
    } else if (error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout')) {
      const path = normalizePath(error.config?.url);
      if (path.includes('/api/v1/auth/login')) {
        notifyRequestError('로그인 응답이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.');
      } else if (path.includes('/api/v1/auth/register')) {
        notifyRequestError('회원가입 처리와 인증 메일 발송이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.');
      } else {
        notifyRequestError('콘텐츠 생성 시간이 예상보다 길어지고 있습니다. 잠시 후 콘텐츠 목록을 새로고침해 주세요.');
      }
      return Promise.reject({
        status: 0,
        message: '요청 시간 초과',
        data: null,
      });
    } else if (error.request) {
      // 요청은 보냈지만 응답이 없음
      notifyRequestError('서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.');
      return Promise.reject({
        status: 0,
        message: '네트워크 연결 오류',
        data: null,
      });
    } else {
      // 요청 설정 중 에러
      notifyRequestError('요청 처리 중 오류가 발생했습니다.');
      return Promise.reject({
        status: 0,
        message: error.message,
        data: null,
      });
    }
  }
);

// API 응답 타입
export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  message: string;
}

// API 에러 응답 타입
export interface ApiErrorResponse {
  error: string;
  message: string;
  detail?: Record<string, unknown>;
}

// API 에러 타입
export interface ApiError {
  status: number;
  message: string;
  data: ApiErrorResponse | null;
}

// 요청 파라미터 타입
export type RequestParams = Record<string, string | number | boolean | undefined | null>;

// 요청 데이터 타입
export type RequestData = Record<string, unknown> | unknown[] | FormData;

// 헬퍼 함수들
export const api = {
  get: <T = unknown>(url: string, params?: RequestParams, config?: AxiosRequestConfig) =>
    apiClient.get<ApiResponse<T>>(url, { ...(config || {}), params }) as unknown as Promise<T>,

  post: <T = unknown>(url: string, data?: RequestData, config?: AxiosRequestConfig) =>
    apiClient.post<ApiResponse<T>>(url, data, config) as unknown as Promise<T>,

  put: <T = unknown>(url: string, data?: RequestData, config?: AxiosRequestConfig) =>
    apiClient.put<ApiResponse<T>>(url, data, config) as unknown as Promise<T>,

  delete: <T = unknown>(url: string) =>
    apiClient.delete<ApiResponse<T>>(url) as unknown as Promise<T>,

  patch: <T = unknown>(url: string, data?: RequestData, config?: AxiosRequestConfig) =>
    apiClient.patch<ApiResponse<T>>(url, data, config) as unknown as Promise<T>,
};

export default apiClient;
